import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EmailField, PhoneField } from '../../components/contact-fields.js';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { quickAddDefaults } from './customer.js';
import { DuplicateNotice, useDuplicates } from './duplicates.js';
import './customers.css';

/**
 * Adding a customer at the counter (SCOPE §9.1).
 *
 * Its own module because two screens need it and they already point at each
 * other: Customers imports the receipt dialog from Sales, so Sales importing
 * the customer dialog back from Customers would be a cycle.
 *
 * **Nothing in particular is required — but something is.** Not first *and*
 * last: "just take my number" is the fastest capture at a counter and refusing
 * it teaches staff to invent names. Not email only either, because a record
 * with no way to recognise the person is a duplicate waiting to happen. The
 * server's rule is `assertIdentifiable`: at least one of name, company, phone
 * or email. This form is the four fields that satisfy it, and whichever one was
 * already typed arrives filled in.
 */

export function NewCustomerDialog({
  term = '',
  onClose,
  onCreated,
}: {
  /**
   * Whatever was already typed — a name, a phone number, or an email.
   *
   * `quickAddDefaults` works out which field it belongs in, so arriving here
   * from a search or from the email-receipt box carries the work across instead
   * of asking for it twice (§2.2-3).
   */
  term?: string;
  onClose: () => void;
  onCreated: (customerId: string) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() => quickAddDefaults(term));

  // Who already has this email or phone, shown before a second record is made (FB-1104).
  const { duplicates, sharesEmail } = useDuplicates(draft.email, draft.phone);

  const create = useMutation({
    mutationFn: () =>
      api.customers.create.mutate({ ...draft, ...(sharesEmail ? { confirmDuplicate: true } : {}) }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
      void onCreated(created.id);
    },
  });

  const identifiable = Object.values(draft).some((value) => value.trim() !== '');

  return (
    <Modal
      open
      title="New customer"
      description="Enough to find them again. The rest can be filled in later."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={create.isPending}
          disabledReason={identifiable ? undefined : 'Enter a name, phone or email'}
          onClick={() => create.mutate()}
        >
          {sharesEmail ? 'Someone else — add anyway' : 'Add customer'}
        </Button>
      }
    >
      <div className="quick-add">
        {(['firstName', 'lastName'] as const).map((field, index) => (
          <div className="field" key={field}>
            <label className="field__label" htmlFor={`new-${field}`}>
              {{ firstName: 'First name', lastName: 'Last name' }[field]}
            </label>
            <input
              id={`new-${field}`}
              className="input"
              autoComplete="off"
              autoFocus={index === 0}
              value={draft[field]}
              onChange={(event) =>
                setDraft((current) => ({ ...current, [field]: event.target.value }))
              }
            />
          </div>
        ))}

        {/*
          The shared fields, not a local copy of their behaviour. This dialog
          had its own formatting and the register's quick-add did not, so the
          same shop got smart phone entry on one screen and raw digits on the
          one it uses two hundred times a day.
        */}
        <PhoneField
          value={draft.phone}
          onChange={(value) => setDraft((current) => ({ ...current, phone: value }))}
        />
        <EmailField
          value={draft.email}
          onChange={(value) => setDraft((current) => ({ ...current, email: value }))}
        />
      </div>

      <DuplicateNotice
        duplicates={duplicates}
        useLabel="Open them"
        onUse={(customerId) => void onCreated(customerId)}
      />

      {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}
    </Modal>
  );
}
